import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSelector } from 'react-redux';
import { selectPage3 } from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { missingField } from 'app/constants';
import { RowSection } from '../Common/RowSection';
import { selectOrderDetailsUIView } from '@AMIEWEB/Order/Store/Order.selectors';
import moment from 'moment';
import { Concatenate } from 'utils/string/string';
import { convertToFormat } from 'app/helpers/dateHelper';

const useStyles = makeStyles()(theme => ({
  boxInlineHeadLabelTxt: {
    'font-weight': '600',
    'margin-top': '0px',
    fontSize: '16px',
  },
  boxInlineSubHeadLabelTxt: {
    fontWeight: '600',
    marginTop: '10px',
    fontSize: '14px',
  },
}));

interface OrderDetailsTwoProps {
  isNewOrderCreation: boolean;
}

const OrderDetailsTwo = (props: OrderDetailsTwoProps) => {
  const { isNewOrderCreation } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();

  const page3Data = useSelector(selectPage3);
  const orderDetailsUIView = useSelector(selectOrderDetailsUIView);
  const orderDetails = orderDetailsUIView?.orderDetails;
  let displayData: {
    placementLength: string;
    asap: string;
    startDate: string;
    endDate: string;
    flexibleDates: string;
    vmsReq: string;
    vmsWebLink: string;
    vmsBillRate: number | string;
    isBlockVmsSync?: string | null;
    blockVmsLastUpdated?: string | null;
    blockVmsLastUpdatedUserName?: string | null;
  };

  function formatDate(date) {
    if (!date && !moment.isDate(date)) {
      return missingField;
    }
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  }

  if (isNewOrderCreation) {
    displayData = {
      placementLength: page3Data.placementLength !== null ? `${page3Data.placementLength} Weeks` : missingField,
      asap: page3Data?.ASAP ? 'Yes' : 'No',
      startDate: formatDate(page3Data?.startDate),
      endDate: formatDate(page3Data?.endDate),
      flexibleDates: page3Data?.flexibleDates ? 'Yes' : 'No',
      vmsReq:
        page3Data.VMSOrderNumber !== null && page3Data.VMSOrderNumber !== '' ? page3Data.VMSOrderNumber : missingField,
      vmsWebLink: page3Data.webLink !== '' ? page3Data.webLink : missingField,
      vmsBillRate:
        page3Data?.VMSBillRate !== null && page3Data?.VMSBillRate !== ''
          ? `${t('currency.dollar')} ${page3Data?.VMSBillRate}`
          : missingField,
    };
  } else {
    displayData = {
      placementLength: orderDetails?.numOfWeeks !== null ? `${orderDetails?.numOfWeeks} Weeks` : missingField,
      asap: orderDetails?.asap ? 'Yes' : 'No',
      startDate: orderDetails?.startDate ? formatDate(orderDetails.startDate) : missingField,
      endDate: orderDetails?.endDate ? formatDate(orderDetails.endDate) : missingField,
      flexibleDates: orderDetails?.flexibleDates ? 'Yes' : 'No',
      vmsReq:
        orderDetails?.vmsOrderNumber !== '' && orderDetails?.vmsOrderNumber !== null
          ? orderDetails.vmsOrderNumber
          : missingField,
      vmsWebLink: orderDetails?.webLink !== '' ? orderDetails.webLink : missingField,
      vmsBillRate:
        orderDetails?.vmsBillRate !== null ? `${t('currency.dollar')} ${orderDetails?.vmsBillRate}` : missingField,
      isBlockVmsSync: orderDetails?.isBlockVmsSync ? 'Yes' : 'No',
      blockVmsLastUpdated: !!orderDetails?.blockVmsLastUpdated
        ? convertToFormat(orderDetails?.blockVmsLastUpdated, 'MM/DD/YYYY h:mm A', true)
        : missingField,
      blockVmsLastUpdatedUserName:
        orderDetails?.blockVmsLastUpdatedFirstName || orderDetails?.blockVmsLastUpdatedLastName
          ? Concatenate([orderDetails?.blockVmsLastUpdatedFirstName, orderDetails?.blockVmsLastUpdatedLastName], ' ')
          : missingField,
    };
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <Grid item direction="column" width="100%">
        <RowSection
          header={
            <div className={classes.boxInlineHeadLabelTxt}>{t('order.newOrderCreation.tabs.placementTerms')}</div>
          }
          data={``}
        />
        <RowSection
          header={t('order.newOrderCreation.orderDetails.placementTerms.placementLength')}
          data={displayData.placementLength}
        />
        <RowSection header={t('order.newOrder.startDate')} data={displayData.startDate} />
        <RowSection header={t('order.newOrder.endDate')} data={displayData.endDate} />
        <RowSection header={t('order.newOrderCreation.orderDetails.placementTerms.asap')} data={displayData.asap} />
        <RowSection
          header={t('order.newOrderCreation.orderDetails.placementTerms.flexibleDates')}
          data={displayData.flexibleDates}
        />

        <Divider style={{ marginBottom: '12px' }} />

        <RowSection
          header={
            <div className={classes.boxInlineHeadLabelTxt}>
              {t('order.newOrderCreation.tabs.technologyInformation')}
            </div>
          }
          data={``}
        />
        <RowSection header={t('order.newOrderCreation.orderDetails.technologyInfo.vmsReq')} data={displayData.vmsReq} />
        <RowSection
          header={t('order.newOrderCreation.orderDetails.technologyInfo.vmsWebLink')}
          data={displayData.vmsWebLink}
          isUrl={true}
        />
        <RowSection
          header={t('order.createUnit.technologyInformation.vmsBillDescription')}
          data={displayData.vmsBillRate}
        />
        {/* BLOCK VMS SECTION START */}
        {!isNewOrderCreation ? (
          <>
            <RowSection
              header={
                <div className={classes.boxInlineSubHeadLabelTxt}>
                  {t('order.createUnit.technologyInformation.blockVMSSync')}
                </div>
              }
              data={<Typography sx={{ fontWeight: 500 }}>{displayData.isBlockVmsSync}</Typography>}
            />
            <RowSection
              header={t('order.createUnit.technologyInformation.lastUpdatedBy')}
              data={displayData.blockVmsLastUpdatedUserName}
            />
            <RowSection
              header={t('order.createUnit.technologyInformation.lastUpdatedDate')}
              data={displayData.blockVmsLastUpdated}
            />
          </>
        ) : (
          <></>
        )}
        {/* BLOCK VMS SECTION END */}
      </Grid>
    </Box>
  );
};

export default OrderDetailsTwo;
